<template>
  <!-- Export Popup Modal -->
  <b-modal v-model="isActive" :can-cancel="['escape', 'x', 'outside']" has-modal-card trap-focus>
    <div class="modal-card export-modal-card">
      <!-- Modal Header -->
      <header class="modal-card-head">
        <p class="modal-card-title">Export Options</p>
        <button class="delete" @click="close"></button>
      </header>

      <!-- Modal Body -->
      <section class="modal-card-body">
        <!-- File Type Section -->
        <div class="box mb-4">
          <h4 class="title is-6">File Type</h4>
          <div class="field">
            <b-radio v-model="form.fileType" native-value="csv">CSV</b-radio>
          </div>
        </div>

        <!-- Organization + Entity Type Section -->
        <div class="box mb-4">
          <div class="columns is-vcentered">
            <div class="column" v-if="isAdmin">
              <h4 class="title is-6">Organization</h4>
              <b-select v-model="form.organization" placeholder="Select organization" expanded>
                <option v-for="org in organizations" :key="org.id" :value="org.id">
                  {{ org.name }}
                </option>
              </b-select>
            </div>

            <div class="column">
              <h4 class="title is-6">Entity Type</h4>
              <b-select v-model="form.entityType" placeholder="Select entity type" expanded>
                <option v-for="entity in entityTypes" :key="entity" :value="entity">
                  {{ entity }}
                </option>
              </b-select>
            </div>
          </div>
        </div>

        <div class="box">
          <b-checkbox v-model="form.includeDeleted" class="m-4">Include Deleted</b-checkbox>
          <b-checkbox v-model="form.includeDraft" class="ml-4">Include Draft</b-checkbox>
          <b-checkbox v-model="form.includeArchived" class="ml-4">Include Archived</b-checkbox>

          <div class="column is-full">
            <span class="has-text-grey">To export all inquiries, leave date fields empty.</span>
          </div>
          <div class="columns">
            <div class="column">
              <b-field label="From">
                <b-datepicker
                  v-model="form.fromDate"
                  placeholder="Select start date"
                  :min-date="minDate"
                  :max-date="maxDate"
                  :date-parser="dateParser"
                  editable
                  locale="en-US"
                  :mobile-modal="false"
                  :mobile-native="false"
                  trap-focus
                  :use-html5-validation="false"
                  icon="calendar-today"
                />
              </b-field>
            </div>
            <div class="column">
              <b-field label="To">
                <b-datepicker
                  v-model="form.toDate"
                  placeholder="Select end date"
                  :min-date="form.fromDate"
                  :max-date="maxDate"
                  :date-parser="dateParser"
                  editable
                  locale="en-US"
                  :mobile-modal="false"
                  :mobile-native="false"
                  trap-focus
                  :use-html5-validation="false"
                  icon="calendar-today"
                />
              </b-field>
            </div>
          </div>
        </div>
      </section>

      <!-- Modal Footer -->
      <footer class="modal-card-foot is-justify-content-space-between">
        <b-button @click="close">Cancel</b-button>
        <b-button type="is-primary" @click="confirmExport">Export</b-button>
      </footer>
    </div>
  </b-modal>
</template>

<script>
import { EXPORT_ENTITIES } from '../constants.js';

export default {
  name: 'ExportConfigPopup',
  props: {
    value: { type: Boolean, default: false },
    organizations: { type: Array, default: () => [] },
    entityTypes: { type: Array, default: () => Object.values(EXPORT_ENTITIES) },
    fileTypes: { type: Array, default: () => ['csv'] },
    isAdmin: { type: Boolean, required: true },
  },
  data() {
    return {
      isActive: this.value,
      form: {
        fileType: 'csv',
        organization: null,
        entityType: 'Inquiry',
        fromDate: null,
        toDate: null,
        includeDeleted: false,
        includeDraft: false,
        includeArchived: false,
      },
    };
  },
  computed: {
    minDate() {
      const date = new Date();
      date.setFullYear(date.getFullYear() - 100);
      return date;
    },

    maxDate() {
      return new Date();
    },
  },
  watch: {
    // keep v-model sync
    value(val) {
      this.isActive = val;
    },
    isActive(val) {
      this.$emit('input', val);

      if (!val) {
        this.close();
      }
    },
    'form.exportAll'(val) {
      if (val) {
        this.form.fromDate = null;
        this.form.toDate = null;
      }
    },
  },
  methods: {
    close() {
      this.isActive = false;
      this.form.organization = null;
      this.form.exportAll = false;
      this.form.fromDate = null;
      this.form.toDate = null;
      this.form.includeDeleted = false;
      this.form.includeDraft = false;
      this.form.includeArchived = false;
    },
    confirmExport() {
      this.$emit('confirm', {
        fileType: this.form.fileType,
        organizationId: this.form.organization,
        entityType: this.form.entityType,
        exportAll: this.form.exportAll,
        startDate: this.form.fromDate,
        endDate: this.form.toDate,
        includeDeleted: this.form.includeDeleted,
        includeDraft: this.form.includeDraft,
        includeArchived: this.form.includeArchived,
      });
      this.close();
    },
  },
};
</script>

<style>
/* Global styles for modal - not scoped because modal renders outside component */
.modal {
  z-index: 9999 !important;
}

.modal-background {
  z-index: 9998 !important;
}

.modal-content,
.modal-card {
  z-index: 9999 !important;
  position: relative;
}
</style>

<style scoped>
.export-modal-card {
  width: 650px;
  max-width: 90vw;
  max-height: 90vh;
  margin: auto;
}

@media (max-width: 768px) {
  .export-modal-card {
    width: 95vw !important;
    max-width: 95vw !important;
    margin: 0.5rem auto !important;
  }

  .export-modal-card :deep(.modal-card-head) {
    padding: 0.75rem 1rem;
  }

  .export-modal-card :deep(.modal-card-title) {
    font-size: 1rem;
  }

  .export-modal-card :deep(.modal-card-body) {
    padding: 0.75rem;
  }

  .export-modal-card :deep(.modal-card-foot) {
    padding: 0.75rem 1rem;
  }

  .export-modal-card :deep(.box) {
    padding: 0.75rem;
    margin-bottom: 0.75rem !important;
  }

  .export-modal-card :deep(.title.is-6) {
    font-size: 0.85rem;
    margin-bottom: 0.5rem;
  }

  .export-modal-card :deep(.field) {
    margin-bottom: 0.5rem;
  }

  .export-modal-card :deep(.b-checkbox) {
    font-size: 0.85rem;
    margin: 0.25rem !important;
  }

  .export-modal-card :deep(.columns) {
    margin: 0;
  }

  .export-modal-card :deep(.column) {
    padding: 0.5rem 0.25rem;
  }

  .export-modal-card :deep(.button) {
    font-size: 0.9rem;
  }
}
</style>
